/**
 * PlateComponentControl — "This plate is for:" on a data-plate photo in the lightbox
 * (Nick, 2026-10-07: "we need our data plates connected to the proper component in our generators data").
 *
 * A toolbar pill shows the photo's current label; tapping it opens a panel listing the unit's components
 * with what we ALREADY HOLD for each (make / model / serial), so the person can compare against the
 * photo on screen, plus "Not sure". Choosing one calls the server (useSetPlateComponent); the server
 * decides who may and what is written. Offline the control is disabled with a plain message: a queued
 * unit-record write has conflict rules nobody has ruled yet.
 *
 * Only for a data-plate photo that is not removed (canTieToComponent). The host passes the unit's doc id.
 *
 * `onWrongUnit` (office viewers only) adds the "Wrong unit" exit: the plate is not this unit's at all, so no
 * component here is right. It closes this panel and hands over to the move dialog (WrongUnitExit).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Tag } from 'lucide-react';
import { Button } from '../../forms/Button';
import { Card } from '../../shared/Card';
import { StatusBadge } from '../../shared/StatusBadge';
import { useGenerator } from '../../../hooks/useGenerator';
import { useIsOffline } from '../../../hooks/useIsOffline';
import { useSetPlateComponent } from '../../../hooks/fieldService/useSetPlateComponent';
import { useCaptureSuggestion } from '../../../hooks/fieldService/useCaptureSuggestion';
import { WrongUnitOffer } from './WrongUnitExit';
import { canTieToComponent, plateChoiceRows, plateLabelText, type PlateComponentChoice } from '../../../lib/fieldService/plateComponents';
import type { GeneratorPhoto } from '../../../types/fieldService/generatorPhotos';

interface PlateComponentControlProps {
  photo: GeneratorPhoto;
  generatorDocId: string;
  /** Office viewers only: open "move this photo to another unit". Absent = no such exit (technicians). */
  onWrongUnit?: () => void;
}

/** Hooks live in the inner component, so a photo that is not a data plate costs no unit read and no mutation setup. */
export function PlateComponentControl({ photo, generatorDocId, onWrongUnit }: PlateComponentControlProps) {
  // key={photo.id}: stepping to the next photo in the viewer starts a closed panel, never one re-labelled under the user.
  return canTieToComponent(photo) ? <PlateComponentPanel key={photo.id} photo={photo} generatorDocId={generatorDocId} onWrongUnit={onWrongUnit} /> : null;
}

function PlateComponentPanel({ photo, generatorDocId, onWrongUnit }: PlateComponentControlProps) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const pillRef = useRef<HTMLButtonElement>(null);
  const firstRowRef = useRef<HTMLDivElement>(null);
  const { data: generator } = useGenerator(generatorDocId);
  const set = useSetPlateComponent(generatorDocId);
  // What the REPORT put this photo under, derived on read (nothing stored): a hint to check against the photo, never a label.
  const suggestion = useCaptureSuggestion(photo, open);
  const offline = useIsOffline();
  // A real modal: Escape closes ONLY this panel (the viewer's own Escape yields to the last aria-modal), focus moves in on
  // open and returns to the pill on close.
  useEffect(() => {
    if (!open) return undefined;
    firstRowRef.current?.querySelector('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      pillRef.current?.focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  const rows = plateChoiceRows(generator);
  const choose = (component: PlateComponentChoice) => {
    set.mutate({ photoId: photo.id, component }, { onSuccess: () => setOpen(false) });
  };

  return (
    <>
      <button
        ref={pillRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={offline ? 'You are offline: you can read what we hold, but saving needs a connection' : 'Which component is this plate for?'}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex min-h-[44px] max-w-[11rem] shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 text-sm text-white transition-colors hover:bg-white/20"
      >
        <Tag className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{plateLabelText(photo)}</span>
      </button>
      {open &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setOpen(false)}>
            <div
              className="max-h-[80vh] w-full max-w-lg overflow-y-auto p-3"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              onClick={(e) => e.stopPropagation()}
              // Tab must not bubble to the viewer behind (this panel is a React child of its toolbar): the viewer's
              // focus ring would pull focus out from under this panel.
              onKeyDown={(e) => {
                if (e.key === 'Tab') e.stopPropagation();
              }}
            >
              <Card>
                <div className="flex items-center justify-between gap-2">
                  <h3 id={titleId} className="text-sm font-bold text-slate-900">This plate is for:</h3>
                  <Button size="lg" variant="ghost" onClick={() => { setOpen(false); pillRef.current?.focus(); }}>Close</Button>
                </div>
                <p className="mb-2 text-xs text-slate-500">Compare with the photo. Under each part is what we hold for this unit.</p>
                {suggestion && (
                  <p className="mb-2 text-xs text-slate-700" data-testid="capture-suggestion">
                    The report this photo came from put it under <strong>{rows.find((r) => r.key === suggestion.component)?.label ?? suggestion.component}</strong>.
                    Check it against the photo; nothing is saved until you choose.
                  </p>
                )}
                {offline && <p className="mb-2 text-sm text-amber-700">You are offline. You can read this, but saving needs a connection.</p>}
                <div className="space-y-1.5" ref={firstRowRef}>
                  {rows.map((row) => {
                    const selected = photo.component === row.key;
                    const held = [row.held.make, row.held.model, row.held.serial ? `S/N ${row.held.serial}` : ''].filter(Boolean).join(' · ');
                    return (
                      <Button
                        key={row.key}
                        fullWidth
                        size="lg"
                        variant={selected ? 'brandTint' : 'secondary'}
                        disabled={offline || set.isPending}
                        onClick={() => choose(row.key)}
                        aria-pressed={selected}
                      >
                        <span className="flex w-full items-center justify-between gap-2 text-left">
                          <span className="min-w-0">
                            <span className="block">{row.label}</span>
                            <span className="block truncate text-xs font-normal text-slate-500">{held || 'Nothing on file'}</span>
                          </span>
                          {selected ? <Check className="h-4 w-4 shrink-0" aria-hidden="true" /> : row.key === suggestion?.component ? <StatusBadge tone="active" size="sm">from the report</StatusBadge> : row.hasPhoto ? <StatusBadge tone="neutral" size="sm">has photo</StatusBadge> : null}
                        </span>
                      </Button>
                    );
                  })}
                  <Button fullWidth size="lg" variant="ghost" disabled={offline || set.isPending} onClick={() => choose('unknown')} aria-pressed={photo.component === 'unknown'}>
                    Not sure
                  </Button>
                  {onWrongUnit && (
                    <WrongUnitOffer photo={photo}>
                      <Button
                        fullWidth
                        size="lg"
                        variant="ghost"
                        disabled={set.isPending}
                        onClick={() => {
                          setOpen(false);
                          onWrongUnit();
                        }}
                      >
                        Wrong unit: this plate belongs to another generator
                      </Button>
                    </WrongUnitOffer>
                  )}
                </div>
                {set.isPending && <p className="mt-2 text-xs text-slate-500">Saving...</p>}
              </Card>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
